/**
 * 软件主题：CSS Token 生成器（纯函数模块）。
 *
 * 把原本 200 行的 createThemeTokens 从 software_theme.ts 抽出，
 * 让主文件只关心状态、调度与 DOM 写入，token 计算在此独立维护。
 *
 * 该模块严格保持纯函数：输入 ThemeSeed，输出一组 CSS 变量键值，
 * 不读取 document、不读取 store、不持有任何模块状态。
 */

import { clamp, hexToRgb, mixHexColor as mixColor, normalizeHexColor, rgbToString, withAlpha as withOpacity } from '@/utils/color'

export type ThemeCssTokens = Record<string, string>

export type ThemeSeed = {
  stage: string
  accent: string
  depth: number
  /**
   * 渲染色调：
   * - 'light'：浅色路径，仅 daylight 预设使用，配套白底壳层与浅色玻璃。
   * - 'dark' / 'auto' / undefined：深色/富色路径，永远以 stage 颜色直出，不做亮度猜测，
   *   保证用户输入什么颜色就看到什么颜色。
   */
  tone?: 'light' | 'dark' | 'auto'
}

const FALLBACK_STAGE = '#6ee7b7'
const FALLBACK_ACCENT = '#8b5cf6'
const FALLBACK_DEPTH = 54

const normalizeSeedDepth = (value: unknown): number => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return FALLBACK_DEPTH
  }

  return Math.round(clamp(value, 0, 100))
}

export const createThemeTokens = (seed: ThemeSeed): ThemeCssTokens => {
  const stage = normalizeHexColor(seed.stage, FALLBACK_STAGE)
  const accent = normalizeHexColor(seed.accent, FALLBACK_ACCENT)
  const depth = normalizeSeedDepth(seed.depth)
  const depthRatio = depth / 100
  const harmonyAccent = mixColor(stage, accent, clamp(0.56 + depthRatio * 0.08, 0.56, 0.66))
  const accentSupport = mixColor(harmonyAccent, '#ffffff', clamp(0.74 - depthRatio * 0.08, 0.62, 0.74))

  const stageRgb = hexToRgb(stage)
  const accentRgb = hexToRgb(harmonyAccent)
  const resolvedTone = seed.tone ?? 'auto'
  // auto 模式永远走深色/富色渲染，不做任何颜色猜测，确保用户设置什么颜色就看到什么颜色。
  // 只有显式 tone:'light'（仅 daylight 预设使用）才走浅色渲染路径。
  const isLightStage = resolvedTone === 'light'
  const stageGlow = mixColor(stage, '#ffffff', clamp(0.1 + (1 - depthRatio) * 0.1, 0.1, 0.2))
  const accentGlow = mixColor(harmonyAccent, '#ffffff', clamp(0.08 + (1 - depthRatio) * 0.08, 0.08, 0.18))
  const stageDark = mixColor(stage, '#07111f', clamp(0.74 - depthRatio * 0.18, 0.45, 0.74))
  const accentDark = mixColor(harmonyAccent, '#08111f', clamp(0.72 - depthRatio * 0.2, 0.42, 0.72))
  const stageCanvas = mixColor(stageDark, accentDark, 0.38)
  const shellBase = mixColor(stage, '#0c1528', clamp(0.58 - depthRatio * 0.08, 0.44, 0.58))
  const shellDeep = mixColor(harmonyAccent, '#0a1020', clamp(0.62 - depthRatio * 0.1, 0.46, 0.62))
  const surfaceBase = mixColor(stage, '#ffffff', clamp(0.82 - depthRatio * 0.08, 0.68, 0.82))
  const surfaceAlt = mixColor(accentSupport, '#ffffff', clamp(0.9 - depthRatio * 0.08, 0.76, 0.9))
  const surfaceStrong = mixColor(surfaceBase, '#ffffff', 0.22)
  const surfaceCard = mixColor(surfaceBase, surfaceAlt, 0.28)
  const surfaceShell = mixColor(shellBase, shellDeep, 0.24)
  const buttonSolid = mixColor(harmonyAccent, '#0d1424', clamp(0.18 + depthRatio * 0.08, 0.18, 0.26))
  const sidebarItemActive = mixColor(surfaceCard, '#ffffff', 0.2)
  const accentPureRgb = hexToRgb(accent)
  const tintSource = isLightStage ? harmonyAccent : stage
  const tintSourceRgb = isLightStage ? accentRgb : stageRgb
  const shellTextPrimary = isLightStage ? '#132033' : '#f4f7ff'
  const shellTextSecondary = isLightStage ? 'rgba(19, 32, 51, 0.72)' : 'rgba(244, 247, 255, 0.74)'
  const shellDivider = isLightStage ? 'rgba(19, 32, 51, 0.12)' : 'rgba(244, 247, 255, 0.16)'
  const lightCanvasBase = mixColor('#f8fbff', accentSupport, 0.36)
  const lightCanvasEdge = mixColor('#edf4ff', harmonyAccent, 0.18)
  const lightCanvasWarm = mixColor('#fff8ef', stage, 0.24)
  const shellSurfaceTop = isLightStage ? mixColor(surfaceCard, '#ffffff', 0.18) : surfaceShell
  const shellSurfaceBottom = isLightStage ? mixColor(surfaceAlt, '#d8e8ff', 0.28) : shellDeep
  const glassGhostBg = isLightStage ? withOpacity(mixColor('#ffffff', accentSupport, 0.24), 0.82) : withOpacity(surfaceShell, 0.2)
  const glassGhostBorder = isLightStage ? withOpacity(mixColor(harmonyAccent, '#ffffff', 0.72), 0.4) : withOpacity('#ffffff', 0.3)
  const glassGhostHoverBg = isLightStage ? withOpacity(mixColor('#ffffff', accentSupport, 0.18), 0.92) : withOpacity('#ffffff', 0.3)
  const glassGhostHoverBorder = isLightStage ? withOpacity(mixColor(harmonyAccent, '#ffffff', 0.56), 0.58) : withOpacity('#ffffff', 0.8)
  const actionBtnShadow = isLightStage ? `0 12px 26px rgba(19, 32, 51, 0.08)` : '0 4px 10px rgba(0, 0, 0, 0.04)'
  const actionBtnShadowHover = isLightStage ? `0 18px 34px rgba(19, 32, 51, 0.14)` : '0 8px 16px rgba(0, 0, 0, 0.08)'
  const actionBtnPrimaryShadow = isLightStage ? `0 16px 34px ${withOpacity(harmonyAccent, 0.22)}, 0 10px 24px rgba(19, 32, 51, 0.08)` : `0 10px 24px ${withOpacity(harmonyAccent, 0.24)}`
  const actionBtnPrimaryShadowHover = isLightStage ? `0 20px 40px ${withOpacity(harmonyAccent, 0.3)}, 0 14px 30px rgba(19, 32, 51, 0.1)` : `0 16px 32px ${withOpacity(harmonyAccent, 0.3)}`

  return {
    '--surface-text-primary': '#182235',
    '--surface-text-secondary': '#66748c',
    '--shell-text-primary': shellTextPrimary,
    '--shell-text-secondary': shellTextSecondary,
    '--shell-divider': shellDivider,
    '--text-inverse': shellTextPrimary,
    '--color-brand-1': stage,
    '--color-brand-1-rgb': rgbToString(stageRgb),
    '--color-brand-2': harmonyAccent,
    '--color-brand-2-rgb': rgbToString(accentRgb),
    '--brand-color': harmonyAccent,
    '--brand-color-pure': isLightStage ? accent : harmonyAccent,
    '--brand-color-rgb': rgbToString(stageRgb),
    '--brand-color-pure-rgb': isLightStage ? rgbToString(accentPureRgb) : rgbToString(accentRgb),
    '--brand-accent-color': isLightStage ? harmonyAccent : stage,
    '--brand-accent-rgb': isLightStage ? rgbToString(accentRgb) : rgbToString(stageRgb),
    '--brand-fn-color': harmonyAccent,
    '--bg-main': isLightStage
      ? `radial-gradient(at 12% 14%, ${withOpacity(mixColor(stage, harmonyAccent, 0.3), 0.18)} 0px, transparent 42%), radial-gradient(at 86% 16%, ${withOpacity(accentGlow, 0.34)} 0px, transparent 34%), radial-gradient(at 18% 88%, ${withOpacity(lightCanvasWarm, 0.52)} 0px, transparent 44%), radial-gradient(at 72% 72%, ${withOpacity('#ffffff', 0.96)} 0px, transparent 40%), linear-gradient(145deg, ${lightCanvasBase} 0%, ${lightCanvasEdge} 56%, ${mixColor('#ffffff', harmonyAccent, 0.14)} 100%)`
      : `radial-gradient(at 0% 0%, ${withOpacity(stage, 1)} 0px, transparent 95%), radial-gradient(at 50% 0%, ${withOpacity(stageGlow, 0.9)} 0px, transparent 50%), radial-gradient(at 0% 100%, ${withOpacity(stage, 0.9)} 0px, transparent 95%), radial-gradient(at 100% 100%, ${withOpacity(harmonyAccent, 0.8)} 0px, transparent 80%), ${stageCanvas}`,
    '--sidebar-item-active': `linear-gradient(180deg, ${withOpacity(mixColor(sidebarItemActive, '#ffffff', 0.14), 0.98)} 0%, ${withOpacity(mixColor(sidebarItemActive, harmonyAccent, 0.08), 0.94)} 100%)`,
    '--app-stage-glow-1': `radial-gradient(circle, ${withOpacity(stageGlow, clamp(0.18 + depthRatio * 0.08, 0.18, 0.3))} 0%, ${withOpacity(stageGlow, 0)} 72%)`,
    '--app-stage-glow-2': `radial-gradient(circle, ${withOpacity(accentGlow, clamp(0.16 + depthRatio * 0.08, 0.16, 0.28))} 0%, ${withOpacity(accentGlow, 0)} 72%)`,
    '--shell-surface': `linear-gradient(180deg, ${withOpacity(shellSurfaceTop, isLightStage ? 0.9 : 0.72)} 0%, ${withOpacity(shellSurfaceBottom, isLightStage ? 0.76 : 0.54)} 100%)`,
    '--panel-surface-connected': withOpacity(surfaceStrong, isLightStage ? 0.2 : 0.14),
    '--panel-surface-connected-strong': withOpacity(surfaceStrong, isLightStage ? 0.28 : 0.22),
    '--glass-overlay-bg': withOpacity(stageCanvas, isLightStage ? 0.18 : 0.22),
    '--glass-surface-bg': withOpacity(surfaceCard, isLightStage ? 0.82 : 0.74),
    '--glass-panel-bg': withOpacity(mixColor(surfaceStrong, '#ffffff', isLightStage ? 0.18 : 0.12), isLightStage ? 0.94 : 0.9),
    '--glass-ghost-bg': glassGhostBg,
    '--glass-ghost-border': glassGhostBorder,
    '--glass-ghost-hover-bg': glassGhostHoverBg,
    '--glass-ghost-hover-border': glassGhostHoverBorder,
    '--panel-line-soft': `1px solid ${withOpacity(mixColor(surfaceStrong, '#ffffff', isLightStage ? 0.22 : 0.16), isLightStage ? 0.32 : 0.24)}`,
    '--panel-line-strong': `1px solid ${withOpacity(mixColor(surfaceStrong, '#ffffff', isLightStage ? 0.14 : 0.08), isLightStage ? 0.42 : 0.34)}`,
    '--header-shell-bg': withOpacity(shellSurfaceTop, isLightStage ? 0.6 : 0.34),
    '--header-back-bg': withOpacity(surfaceStrong, 0.18),
    '--header-back-hover-bg': withOpacity('#ffffff', 0.92),
    '--header-back-border': withOpacity(mixColor(surfaceStrong, '#ffffff', 0.12), 0.26),
    '--header-toolbar-bg': withOpacity(shellSurfaceBottom, isLightStage ? 0.34 : 0.24),
    '--header-toolbar-border': isLightStage ? withOpacity(mixColor(harmonyAccent, '#ffffff', 0.7), 0.26) : withOpacity('#ffffff', 0.16),
    '--header-icon-hover-bg': withOpacity('#ffffff', 0.44),
    '--header-icon-hover-border': withOpacity('#ffffff', 0.34),
    '--header-entry-bg': withOpacity(surfaceStrong, 0.16),
    '--header-entry-hover-bg': withOpacity('#ffffff', 0.9),
    '--header-dropdown-bg': withOpacity(mixColor(surfaceStrong, '#ffffff', 0.1), 0.96),
    '--header-dropdown-border': withOpacity(mixColor(surfaceStrong, '#ffffff', 0.08), 0.58),
    '--settings-shell-bg': withOpacity(shellSurfaceTop, isLightStage ? 0.62 : 0.36),
    '--settings-shell-border': isLightStage ? withOpacity(mixColor(harmonyAccent, '#ffffff', 0.74), 0.3) : withOpacity('#ffffff', 0.22),
    '--settings-sidebar-bg': withOpacity(mixColor(shellSurfaceTop, stage, 0.06), isLightStage ? 0.58 : 0.38),
    '--settings-sidebar-border': isLightStage ? withOpacity(mixColor(harmonyAccent, '#ffffff', 0.78), 0.24) : withOpacity('#ffffff', 0.18),
    '--settings-sidebar-item-hover-bg': withOpacity('#ffffff', 0.12),
    '--settings-sidebar-item-active-bg': `linear-gradient(180deg, ${withOpacity(mixColor(sidebarItemActive, '#ffffff', 0.18), 0.98)} 0%, ${withOpacity(mixColor(sidebarItemActive, harmonyAccent, 0.1), 0.94)} 100%)`,
    '--settings-sidebar-icon-bg': withOpacity(harmonyAccent, 0.12),
    '--settings-right-bg': withOpacity(mixColor(surfaceStrong, '#ffffff', isLightStage ? 0.18 : 0.12), 0.98),
    '--settings-panel-soft-bg': withOpacity(tintSource, isLightStage ? 0.08 : 0.06),
    '--settings-panel-soft-border': withOpacity(tintSource, isLightStage ? 0.18 : 0.14),
    '--settings-card-bg': withOpacity(mixColor(surfaceCard, '#ffffff', isLightStage ? 0.24 : 0.18), isLightStage ? 0.96 : 0.92),
    '--settings-card-border': withOpacity(mixColor(surfaceStrong, '#ffffff', isLightStage ? 0.14 : 0.08), isLightStage ? 0.38 : 0.34),
    '--action-btn-primary-bg': isLightStage ? `linear-gradient(135deg, ${harmonyAccent} 0%, ${accent} 100%)` : harmonyAccent,
    '--action-btn-primary-bg-solid': isLightStage ? mixColor(accent, '#0d1424', 0.1) : buttonSolid,
    '--action-btn-primary-accent-rgb': rgbToString(tintSourceRgb),
    '--action-btn-primary-soft-bg': withOpacity(mixColor(tintSource, harmonyAccent, 0.26), 0.16),
    '--action-btn-primary-soft-border': withOpacity(mixColor(tintSource, harmonyAccent, 0.4), 0.26),
    '--action-btn-shadow': actionBtnShadow,
    '--action-btn-shadow-hover': actionBtnShadowHover,
    '--action-btn-primary-shadow': actionBtnPrimaryShadow,
    '--action-btn-primary-shadow-hover': actionBtnPrimaryShadowHover,
    '--btn-base-bg': withOpacity(mixColor(surfaceCard, '#ffffff', isLightStage ? 0.28 : 0.24), isLightStage ? 0.96 : 0.9),
    '--btn-base-hover-bg': withOpacity(tintSource, 0.08),
    '--btn-base-hover-text': buttonSolid,
    '--btn-base-border-color': withOpacity(mixColor(surfaceStrong, '#ffffff', isLightStage ? 0.14 : 0.08), isLightStage ? 0.34 : 0.28),
    '--btn-base-hover-border-color': withOpacity(tintSource, 0.26),
    '--btn-primary-text': '#ffffff',
    '--action-btn-ghost-text': shellTextPrimary,
    '--action-btn-ghost-bg': isLightStage ? withOpacity(mixColor('#ffffff', accentSupport, 0.16), 0.92) : 'rgba(255, 255, 255, 0.8)',
    '--action-btn-ghost-border': isLightStage ? `1px solid ${withOpacity(harmonyAccent, 0.14)}` : '1px solid rgba(0, 0, 0, 0.05)',
    '--sidebar-surface': isLightStage ? withOpacity(harmonyAccent, 0.07) : `rgba(${rgbToString(stageRgb)}, 0.12)`,
    '--sidebar-surface-hover': isLightStage ? withOpacity(harmonyAccent, 0.13) : `rgba(${rgbToString(stageRgb)}, 0.2)`,
    '--bg-card': isLightStage ? '#ffffff' : 'rgba(255, 255, 255, 0.9)',
    '--border-color': isLightStage ? withOpacity(mixColor(harmonyAccent, '#132033', 0.3), 0.1) : 'rgba(0, 0, 0, 0.08)',
    '--vk-accent-shadow-soft': isLightStage ? `0 0 18px ${withOpacity(harmonyAccent, 0.24)}` : `0 0 12px ${withOpacity(stage, 0.45)}`,
    '--card-shadow': isLightStage ? '0 1px 6px rgba(117, 117, 117, 0.07), 0 0 0 1px rgba(88, 88, 88, 0.07)' : 'none',
    '--icon-wrapper-bg': isLightStage ? 'rgba(19, 32, 51, 0.05)' : '#f2f2f2',
    '--pagination-item-bg': isLightStage ? 'rgba(255, 255, 255, 0.92)' : 'rgba(255, 255, 255, 0.6)',
    '--is-light-stage': isLightStage ? '1' : '0',
  }
}

/**
 * 从已渲染的 token 表中读取 `--is-light-stage` 标志。
 * 与 createThemeTokens 内部的 isLightStage 计算保持一致，避免重复推导。
 */
export const isLightStageTokens = (tokens: ThemeCssTokens | null): boolean => {
  return tokens?.['--is-light-stage'] === '1'
}
